import React, { useState, useEffect, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { ProgressBar } from 'primereact/progressbar';
import { Checkbox } from 'primereact/checkbox';
import { getImportModules, downloadTemplate, validateImport, commitImport } from '../../api/importApi';
import './DataImportPage.scss';

const STEPS = ['Choose data', 'Upload & check', 'Import'];

const ACTION_TAG = {
  create: { label: 'Will import', severity: 'success' },
  skip: { label: 'Skipped', severity: 'warning' },
  error: { label: 'Error', severity: 'danger' },
};

const DataImportPage = () => {
  const [modules, setModules] = useState([]);
  const [selected, setSelected] = useState(null);
  const [file, setFile] = useState(null);
  const [validation, setValidation] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [skipInvalid, setSkipInvalid] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    getImportModules()
      .then(res => setModules(res.modules || []))
      .catch(err => toast.error(err.response?.data?.message || 'Could not load import options'));
  }, []);

  const step = result ? 2 : selected ? 1 : 0;

  const grouped = useMemo(() => {
    const map = new Map();
    for (const m of modules) {
      if (!map.has(m.group)) map.set(m.group, []);
      map.get(m.group).push(m);
    }
    return Array.from(map.entries());
  }, [modules]);

  const reset = () => {
    setSelected(null); setFile(null); setValidation(null);
    setResult(null); setSkipInvalid(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const backToUpload = () => {
    setFile(null); setValidation(null); setResult(null); setSkipInvalid(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleTemplate = async (moduleKey, format) => {
    try {
      await downloadTemplate(moduleKey, format);
    } catch {
      toast.error('Could not download the template');
    }
  };

  const handleFile = async (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    setFile(picked);
    setValidation(null);
    setResult(null);
    setBusy(true);
    try {
      const res = await validateImport(selected.key, picked);
      setValidation(res);
      if (res.summary.errors > 0) {
        toast.warn(`${res.summary.errors} row(s) need attention before importing`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not read that file');
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async () => {
    setBusy(true);
    try {
      const res = await commitImport(selected.key, file, { skipInvalid });
      setResult(res);
      toast.success(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const canImport =
    validation &&
    validation.summary.toCreate > 0 &&
    (validation.summary.errors === 0 || skipInvalid);

  // ---- Step 0: pick a module -------------------------------------------------
  const renderPicker = () => (
    <>
      <p className="text-color-secondary mt-0">
        Choose what you want to import. Download the template, fill it in, then upload it —
        nothing is saved until you have reviewed the check results.
      </p>
      {grouped.map(([group, items]) => (
        <div key={group} className="mb-4">
          <h3 className="text-lg mt-0 mb-2">{group}</h3>
          <div className="grid">
            {items.map(m => (
              <div key={m.key} className="col-12 md:col-6 lg:col-4">
                <div className="import-card h-full flex flex-column">
                  <div className="font-semibold mb-1">{m.label}</div>
                  <div className="text-sm text-color-secondary flex-grow-1 mb-3">{m.description}</div>
                  <div className="flex gap-2 flex-wrap">
                    <Button label="Import" size="small" onClick={() => setSelected(m)} />
                    <Button label="Excel" icon="pi pi-download" size="small" text
                      onClick={() => handleTemplate(m.key, 'xlsx')} />
                    <Button label="CSV" icon="pi pi-download" size="small" text
                      onClick={() => handleTemplate(m.key, 'csv')} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );

  // ---- Step 1: upload and review --------------------------------------------
  const renderUpload = () => (
    <>
      <div className="flex align-items-center gap-2 mb-3">
        <Button icon="pi pi-arrow-left" label="Back" text size="small" onClick={reset} />
        <span className="font-semibold text-lg">{selected.label}</span>
      </div>

      <div className="grid">
        <div className="col-12 lg:col-8">
          <div className="import-drop mb-3">
            <i className="pi pi-file-excel text-3xl text-color-secondary mb-2" />
            <p className="m-0 mb-2">Upload your filled-in .xlsx or .csv file</p>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={handleFile} disabled={busy} />
            {file && <div className="text-sm text-color-secondary mt-2">{file.name}</div>}
          </div>

          {busy && !validation && <ProgressBar mode="indeterminate" style={{ height: 6 }} />}

          {validation && (
            <>
              <div className="flex gap-2 flex-wrap mb-3">
                <Tag severity="success" value={`${validation.summary.toCreate} will import`} />
                {validation.summary.toSkip > 0 && (
                  <Tag severity="warning" value={`${validation.summary.toSkip} skipped`} />
                )}
                {validation.summary.errors > 0 && (
                  <Tag severity="danger" value={`${validation.summary.errors} with errors`} />
                )}
                <Tag severity="info" value={`${validation.summary.total} rows read`} />
              </div>

              {validation.unknownColumns?.length > 0 && (
                <Message className="w-full mb-3" severity="info"
                  text={`Ignored unrecognised column(s): ${validation.unknownColumns.join(', ')}`} />
              )}

              <DataTable value={validation.rows} size="small" paginator rows={15} scrollable
                rowClassName={r => (r.action === 'error' ? 'import-row-error' : '')}>
                <Column header="Row" body={r => r.rowNumber} style={{ width: 70 }} />
                <Column header="Status" style={{ width: 130 }} body={r => {
                  const t = ACTION_TAG[r.action];
                  return <Tag severity={t.severity} value={t.label} />;
                }} />
                {selected.columns.slice(0, 4).map(c => (
                  <Column key={c.key} header={c.key} body={r => r.data?.[c.key] ?? '—'} />
                ))}
                <Column header="Details" body={r => (
                  <div className="text-sm">
                    {r.errors.map((e, i) => (
                      <div key={`e${i}`} style={{ color: 'var(--red-500)' }}>{e}</div>
                    ))}
                    {r.warnings.map((w, i) => (
                      <div key={`w${i}`} style={{ color: 'var(--orange-500)' }}>{w}</div>
                    ))}
                    {!r.errors.length && !r.warnings.length && <span className="text-color-secondary">—</span>}
                  </div>
                )} />
              </DataTable>

              <div className="flex align-items-center gap-3 mt-3 flex-wrap">
                {validation.summary.errors > 0 && (
                  <div className="flex align-items-center gap-2">
                    <Checkbox inputId="skipInvalid" checked={skipInvalid}
                      onChange={e => setSkipInvalid(e.checked)} />
                    <label htmlFor="skipInvalid" className="text-sm">
                      Import the valid rows and skip the {validation.summary.errors} with errors
                    </label>
                  </div>
                )}
                <Button label={`Import ${validation.summary.toCreate} row(s)`} icon="pi pi-check"
                  disabled={!canImport} loading={busy} onClick={handleImport} className="ml-auto" />
              </div>
            </>
          )}
        </div>

        {/* Column reference — saves a trip back to the template */}
        <div className="col-12 lg:col-4">
          <div className="import-reference">
            <div className="font-semibold mb-2">Expected columns</div>
            <div className="flex gap-2 mb-3">
              <Button label="Excel template" icon="pi pi-download" size="small" outlined
                onClick={() => handleTemplate(selected.key, 'xlsx')} />
              <Button label="CSV" icon="pi pi-download" size="small" outlined
                onClick={() => handleTemplate(selected.key, 'csv')} />
            </div>
            <ul className="list-none p-0 m-0">
              {selected.columns.map(c => (
                <li key={c.key} className="mb-2 pb-2 border-bottom-1 surface-border">
                  <code className="text-sm">{c.key}</code>
                  {c.required && <Tag className="ml-2" severity="danger" value="required" />}
                  {c.options && (
                    <div className="text-xs text-color-secondary mt-1">One of: {c.options.join(', ')}</div>
                  )}
                  {c.note && <div className="text-xs text-color-secondary mt-1">{c.note}</div>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  );

  // ---- Step 2: result --------------------------------------------------------
  const renderResult = () => (
    <div className="text-center py-5">
      <i className="pi pi-check-circle text-5xl mb-3" style={{ color: 'var(--green-500)' }} />
      <h2 className="mt-0">{result.message}</h2>
      <div className="flex gap-2 justify-content-center flex-wrap mb-4">
        <Tag severity="success" value={`${result.createdCount} imported`} />
        {result.skippedCount > 0 && <Tag severity="warning" value={`${result.skippedCount} skipped`} />}
        {result.errorCount > 0 && <Tag severity="danger" value={`${result.errorCount} had errors`} />}
      </div>

      {result.failed?.length > 0 && (
        <div className="text-left mx-auto" style={{ maxWidth: 640 }}>
          <Message className="w-full mb-2" severity="warn"
            text={`${result.failed.length} row(s) could not be saved:`} />
          <ul className="text-sm">
            {result.failed.map(f => (
              <li key={f.rowNumber}>Row {f.rowNumber}: {f.message}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2 justify-content-center mt-4">
        <Button label="Import more of the same" outlined onClick={backToUpload} />
        <Button label="Done" onClick={reset} />
      </div>
    </div>
  );

  return (
    <div className="p-3">
      <Card>
        <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h1 className="text-2xl m-0">Data Import</h1>
            <p className="text-color-secondary m-0 mt-1 text-sm">
              Bulk-load your school's data from a spreadsheet.
            </p>
          </div>
          <div className="flex gap-3 text-sm">
            {STEPS.map((s, i) => (
              <span key={s} className={i === step ? 'font-semibold' : 'text-color-secondary'}>
                {i + 1}. {s}
              </span>
            ))}
          </div>
        </div>

        {step === 0 && renderPicker()}
        {step === 1 && renderUpload()}
        {step === 2 && renderResult()}
      </Card>
    </div>
  );
};

export default DataImportPage;
